import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { SERVICES } from "../../lib/content";
import { Switch } from "../../components/ui/switch";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3 outline-none focus:border-[#FF1F8E]";

const Card = ({ title, sub, children, id }) => (
  <section className="space-y-4 rounded-3xl bg-white p-6 ring-1 ring-[#EBD08A] sm:p-8" data-testid={`site-section-${id}`}>
    <div><h2 className="font-display text-xl font-bold sm:text-2xl">{title}</h2>{sub && <p className="text-sm text-black/55">{sub}</p>}</div>
    {children}
  </section>
);

const F = ({ t, hint, children }) => (
  <label className="block"><span className="mb-1 block text-sm font-semibold">{t}</span>{children}{hint && <span className="mt-1 block text-xs text-black/50">{hint}</span>}</label>
);

const toForm = (s) => ({ ...s, services: Object.fromEntries(SERVICES.map((x) => [x.slug, { blurb: s.services[x.slug].blurb, itemsText: s.services[x.slug].items.join("\n") }])) });

const Payments = ({ list, onChange }) => {
  const [v, setV] = useState("");
  const add = () => { if (v.trim() && !list.includes(v.trim())) onChange([...list, v.trim()]); setV(""); };
  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid="site-payments-list">
        {list.map((p) => (
          <span key={p} className="flex items-center gap-1 rounded-full bg-[#FFF4F8] py-1.5 pl-4 pr-2 text-sm font-semibold">{p}
            <button type="button" onClick={() => onChange(list.filter((x) => x !== p))} aria-label={`Remove ${p}`} className="rounded-full p-1 hover:bg-white" data-testid={`site-payment-remove-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className={field} placeholder="e.g. Zelle" data-testid="site-payment-input" />
        <button type="button" onClick={add} className="btn-outline-gold !px-4 !py-2" data-testid="site-payment-add"><Plus className="h-4 w-4" />Add</button>
      </div>
    </div>
  );
};

export default function AdminSiteInfo() {
  const { site, setSite } = useSite();
  const [f, setF] = useState(() => toForm(site));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setIn = (grp, k) => (e) => setF({ ...f, [grp]: { ...f[grp], [k]: e.target.value } });
  const setSvc = (slug, k) => (e) => setF({ ...f, services: { ...f.services, [slug]: { ...f.services[slug], [k]: e.target.value } } });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const services = Object.fromEntries(Object.entries(f.services).map(([k, v]) => [k, { blurb: v.blurb, items: v.itemsText.split("\n").map((x) => x.trim()).filter(Boolean) }]));
    try {
      const { data } = await api.put("/admin/settings", { ...f, services });
      setSite(data);
      setF(toForm(data));
      toast.success("Your website is updated");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="mx-auto max-w-3xl space-y-6 pb-24" data-testid="site-info-form">
      <div>
        <h1 className="font-display text-3xl font-bold">Site info</h1>
        <p className="mt-1 text-sm text-black/55">Change your banner, contact details, payment options and page text. Tap Save at the bottom when you're done.</p>
      </div>

      <Card title="Top banner" sub="The pink strip across the top of every page." id="banner">
        <label className="flex items-center gap-3 text-sm font-semibold">
          <Switch checked={f.banner.enabled} onCheckedChange={(v) => setF({ ...f, banner: { ...f.banner, enabled: v } })} data-testid="site-banner-switch" />{f.banner.enabled ? "Showing" : "Hidden"}
        </label>
        <F t="Message"><input value={f.banner.text} onChange={setIn("banner", "text")} maxLength={160} className={field} placeholder="e.g. Christmas orders close Dec 10!" data-testid="site-banner-text" /></F>
        <div className="grid gap-4 sm:grid-cols-2">
          <F t="Link (optional)" hint="https://… or /order for your order form"><input value={f.banner.link_url} onChange={setIn("banner", "link_url")} className={field} data-testid="site-banner-link" /></F>
          <F t="Link words"><input value={f.banner.link_text} onChange={setIn("banner", "link_text")} className={field} placeholder="Order now" data-testid="site-banner-link-text" /></F>
        </div>
      </Card>

      <Card title="Contact & social" sub="Shown on the Contact page, footer and customer emails. New-order alerts still go to kmkcustomsllc@yahoo.com." id="contact">
        <div className="grid gap-4 sm:grid-cols-2">
          <F t="Phone"><input required value={f.contact.phone} onChange={setIn("contact", "phone")} className={field} data-testid="site-phone-input" /></F>
          <F t="Email"><input required type="email" value={f.contact.email} onChange={setIn("contact", "email")} className={field} data-testid="site-email-input" /></F>
          <F t="Facebook link"><input value={f.contact.facebook} onChange={setIn("contact", "facebook")} className={field} placeholder="https://facebook.com/…" data-testid="site-facebook-input" /></F>
          <F t="TikTok link"><input value={f.contact.tiktok} onChange={setIn("contact", "tiktok")} className={field} placeholder="https://tiktok.com/@…" data-testid="site-tiktok-input" /></F>
          <F t="TikTok name"><input value={f.contact.tiktok_handle} onChange={setIn("contact", "tiktok_handle")} className={field} placeholder="@name" data-testid="site-tiktok-handle-input" /></F>
        </div>
      </Card>

      <Card title="Payment options" sub="What customers can pick on the order form. Never add bank account numbers here." id="payments">
        <Payments list={f.payments} onChange={(payments) => setF({ ...f, payments })} />
      </Card>

      <Card title="Page text" sub="Leave a blank line between paragraphs." id="pages">
        <F t="About page: our story"><textarea rows={8} value={f.about_story} onChange={set("about_story")} className={field} data-testid="site-about-story" /></F>
        <F t="About page: thank-you note"><textarea rows={3} value={f.about_thank_you} onChange={set("about_thank_you")} className={field} data-testid="site-about-thanks" /></F>
        <F t="Order form intro"><textarea rows={3} value={f.order_form_intro} onChange={set("order_form_intro")} className={field} data-testid="site-order-intro" /></F>
        <F t="Ordering page: turnaround" hint="Also included in the customer's thank-you email."><textarea rows={3} value={f.ordering_turnaround} onChange={set("ordering_turnaround")} className={field} data-testid="site-turnaround" /></F>
        <F t="Ordering page: payment policy"><textarea rows={2} value={f.ordering_payment} onChange={set("ordering_payment")} className={field} data-testid="site-payment-policy" /></F>
      </Card>

      <Card title="Services" sub="The description and list on each Services section (one item per line)." id="services">
        {SERVICES.map((s) => (
          <div key={s.slug} className="space-y-3 border-t border-dashed border-[#EBD08A] pt-4 first:border-0 first:pt-0">
            <p className="font-semibold">{s.title}</p>
            <textarea rows={2} value={f.services[s.slug].blurb} onChange={setSvc(s.slug, "blurb")} className={field} data-testid={`site-service-blurb-${s.slug}`} />
            <textarea rows={4} value={f.services[s.slug].itemsText} onChange={setSvc(s.slug, "itemsText")} className={field} data-testid={`site-service-items-${s.slug}`} />
          </div>
        ))}
      </Card>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#EBD08A] bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl justify-end">
          <button disabled={busy} className="btn-pink !py-3 disabled:opacity-60" data-testid="site-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save changes</button>
        </div>
      </div>
    </form>
  );
}
